<script lang="ts">
  import {
    ChevronDown,
    FolderOpen,
    MessageSquare,
    Moon,
    Plus,
    Settings,
    Sun,
    Trash2,
    Upload,
    LogOut,
    FileText,
    Image as ImageIcon,
    FileSpreadsheet,
    FileSearch,
    Eye,
    Download,
    Square,
    Bug,
    Copy,
    Check,
    Link2,
    MousePointer2,
  } from "lucide-svelte";
  import { onDestroy } from "svelte";
  import excelApiDts from "../docs/excel-officejs-api.d.ts?raw";
  import { ChatController } from "../../chat/chat-controller";
  import { setChatContext } from "../../chat/chat-runtime-context";
  import ChatInput from "./ChatInput.svelte";
  import MessageList from "./MessageList.svelte";
  import SettingsPanel from "./SettingsPanel.svelte";
  import { authStore } from "../../auth/auth-store";
  import type { ChatTab } from "../../chat/types";
  import { analyzeSheetStructureForLLM } from "../../excel/api";

  import { i18n } from "../../chat/i18n.svelte";
  const { t } = i18n;

  type Theme = "light" | "dark";
  const THEME_KEY = "excel-agent-theme";

  const adapter = {
    get emptyStateMessage() { return t("emptyStateMessage"); },
    handleLinkClick: () => "default",
    hasImageSearch: false,
    staticFiles: {
      "/home/user/docs/excel-officejs-api.d.ts": excelApiDts,
    },
  };

  const controller = (() => {
    const ctrl = new ChatController(adapter);
    setChatContext(ctrl);
    return ctrl;
  })();

  const runtimeState = controller.state;

  let activeTab = $state<ChatTab>("chat");
  let isDragOver = $state(false);
  let dragCounter = $state(0);
  let sessionDropdownOpen = $state(false);
  let filesDropdownOpen = $state(false);
  let previewFile = $state<any>(null);

  let theme = $state<Theme>(loadTheme());

  let debugOutput = $state("");
  let isAnalyzing = $state(false);
  let showCopyFeedback = $state(false);

  function loadTheme(): Theme {
    const saved = localStorage.getItem(THEME_KEY) as Theme | null;
    const initial = saved ?? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
    document.documentElement.setAttribute("data-theme", initial);
    return initial;
  }

  function toggleTheme() {
    theme = theme === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem(THEME_KEY, theme);
  }

  function formatTokens(value: number | undefined): string {
    if (!value) return "0";
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
    return value.toString();
  }

  function formatCost(value: number | undefined): string {
    if (!value) return "$0.00";
    if (value < 0.01) return `$${value.toFixed(4)}`;
    return `$${value.toFixed(3)}`;
  }

  function switchToTab(tab: ChatTab) {
    console.log(`[MainApp] switchToTab: ${activeTab} → ${tab}`);
    activeTab = tab;
    sessionDropdownOpen = false;
    previewFile = null;
  }

  async function runStructureAnalysis() {
    isAnalyzing = true;
    try {
      const result = await analyzeSheetStructureForLLM();
      debugOutput = JSON.stringify(result, null, 2);
    } catch (err: any) {
      debugOutput = `Error: ${err.message || String(err)}`;
    } finally {
      isAnalyzing = false;
    }
  }

  function copyToClipboard() {
    if (!debugOutput) return;
    navigator.clipboard.writeText(debugOutput).then(() => {
      showCopyFeedback = true;
      setTimeout(() => showCopyFeedback = false, 2000);
    });
  }

  $effect(() => {
    console.log(`[MainApp] activeTab is now: ${activeTab}`);
  });

  $effect(() => {
    console.log(`[MainApp] current uploads:`, $runtimeState.uploads.map(u => ({ id: u.id, name: u.name })));
  });

  async function handleNewSession() {
    await controller.newSession();
    switchToTab("chat");
  }

  function handleDragEnter(event: DragEvent) {
    event.preventDefault();
    dragCounter += 1;
    isDragOver = true;
  }

  function handleDragOver(event: DragEvent) {
    // Required: must preventDefault on dragover or the browser won't fire drop
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
  }

  function handleDragLeave(event: DragEvent) {
    event.preventDefault();
    dragCounter -= 1;
    if (dragCounter === 0) isDragOver = false;
  }

  async function handleDrop(event: DragEvent) {
    event.preventDefault();
    dragCounter = 0;
    isDragOver = false;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    // Switch to chat tab so the user sees the upload feedback
    if (activeTab !== "chat") {
      switchToTab("chat");
    }

    // Upload each dropped file through the same path as the file input
    for (const file of Array.from(files)) {
      try {
        await controller.handleUpload(file);
      } catch (err) {
        console.error("[MainApp] Drop upload failed:", file.name, err);
      }
    }
  }

  onDestroy(() => {});

  const truncatedName = $derived(
    ($runtimeState.currentSession?.name || "New Chat").length > 22
      ? `${($runtimeState.currentSession?.name || "New Chat").slice(0, 20)}…`
      : ($runtimeState.currentSession?.name || "New Chat")
  );
</script>

<div
  role="application"
  class="flex flex-col flex-1 min-h-0 h-full relative overflow-hidden"
  style="background: var(--chat-bg); font-family: var(--chat-font-sans);"
  ondragenter={handleDragEnter}
  ondragover={handleDragOver}
  ondragleave={handleDragLeave}
  ondrop={handleDrop}
>
  <!-- Fluent Header -->
  <header style="
    background: var(--chat-bg-secondary);
    border-bottom: 1px solid var(--chat-border);
    flex-shrink: 0;
    z-index: 20;
  ">
    <!-- Tab Row -->
    <div class="flex items-center justify-between px-1" style="height: 44px;">
      <!-- Pill Navigation -->
      <nav class="flex items-center gap-0.5 px-1">
        <!-- Chat tab + session chevron grouped together -->
        <div class="flex items-center gap-0" style="position: relative;">
          <button
            type="button"
            onclick={() => switchToTab("chat")}
            class="flex items-center gap-1.5 py-1.5 px-3 text-xs font-medium transition-all"
            style="
              border-radius: var(--chat-radius) 0 0 var(--chat-radius);
              background: {activeTab === 'chat' ? 'var(--chat-accent-subtle)' : 'transparent'};
              color: {activeTab === 'chat' ? 'var(--chat-accent)' : 'var(--chat-text-muted)'};
              min-width: 80px;
              max-width: 140px;
            "
          >
            <MessageSquare size={13} style="flex-shrink: 0;" />
            <span class="truncate">{truncatedName === "New Chat" ? t("newChat") : truncatedName}</span>
          </button>
          <button
            type="button"
            onclick={() => (sessionDropdownOpen = !sessionDropdownOpen)}
            class="flex items-center justify-center py-1.5 px-1.5 transition-all"
            style="
              border-radius: 0 var(--chat-radius) var(--chat-radius) 0;
              background: {activeTab === 'chat' || sessionDropdownOpen ? 'var(--chat-accent-subtle)' : 'transparent'};
              color: {activeTab === 'chat' ? 'var(--chat-accent)' : 'var(--chat-text-muted)'};
              border-left: 1px solid {activeTab === 'chat' ? 'var(--chat-border-active)' : 'transparent'};
            "
            title={t("switchSession")}
          >
            <ChevronDown size={11} style="transition: transform 0.2s; transform: {sessionDropdownOpen ? 'rotate(180deg)' : 'none'};" />
          </button>

          {#if sessionDropdownOpen}
            <div
              role="menu"
              tabindex="-1"
              class="fluent-fade-in absolute top-full left-0 mt-1 flex flex-col"
              style="
                width: 240px;
                background: var(--chat-bg-tertiary);
                border: 1px solid var(--chat-border-active);
                border-radius: var(--chat-radius-lg);
                box-shadow: var(--chat-shadow-lg);
                z-index: 50;
                overflow: hidden;
                padding: 4px;
              "
              onmouseleave={() => (sessionDropdownOpen = false)}
            >
              <button
                type="button"
                onclick={handleNewSession}
                class="flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-all text-left"
                style="
                  color: var(--chat-accent);
                  border-radius: var(--chat-radius);
                  background: transparent;
                "
                onmouseenter={(e) => (e.currentTarget.style.background = 'var(--chat-accent-subtle)')}
                onmouseleave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <Plus size={13} />
                {t("newChat")}
              </button>

              {#if $runtimeState.sessions.length > 0}
                <div style="height: 1px; background: var(--chat-border); margin: 4px 0;"></div>
              {/if}

              <div style="max-height: 260px; overflow-y: auto;">
                {#if $runtimeState.sessions.length === 0}
                  <div class="px-3 py-4 text-xs text-center" style="color: var(--chat-text-muted); font-style: italic;">{t("noHistory")}</div>
                {:else}
                  {#each $runtimeState.sessions as s (s.id)}
                    <button
                      type="button"
                      onclick={(e) => { 
                        e.stopPropagation();
                        controller.switchSession(s.id);
                        switchToTab("chat");
                      }}
                      class="w-full px-3 py-2 text-xs text-left transition-all flex flex-col gap-0.5"
                      style="
                        border-radius: var(--chat-radius);
                        background: {$runtimeState.currentSession?.id === s.id ? 'var(--chat-accent-subtle)' : 'transparent'};
                      "
                      onmouseenter={(e) => { if ($runtimeState.currentSession?.id !== s.id) (e.currentTarget as HTMLElement).style.background = 'var(--chat-bg-overlay)'; }}
                      onmouseleave={(e) => { if ($runtimeState.currentSession?.id !== s.id) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                    >
                      <div class="flex items-center justify-between gap-2">
                        <span class="font-medium truncate" style="color: var(--chat-text-primary);">{s.name || t("untitledChat")}</span>
                        {#if $runtimeState.currentSession?.id === s.id}
                          <div style="width: 6px; height: 6px; border-radius: 99px; background: var(--chat-accent); flex-shrink: 0;"></div>
                        {/if}
                      </div>
                      <span style="color: var(--chat-text-muted); font-size: 10px;">
                        {new Date(s.updatedAt).toLocaleDateString(i18n.locale === 'vi' ? 'vi-VN' : 'en-US')} {new Date(s.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </button>
                  {/each}
                {/if}
              </div>
            </div>
          {/if}
        </div>

        <div style="width: 1px; height: 16px; background: var(--chat-border); margin: 0 4px;"></div>

        <!-- Connected Files Dropdown -->
        <div class="flex items-center gap-0" style="position: relative;">
          <button
            type="button"
            onclick={() => (filesDropdownOpen = !filesDropdownOpen)}
            class="flex items-center gap-1.5 py-1.5 px-3 text-xs font-medium transition-all"
            style="
              border-radius: var(--chat-radius);
              background: {filesDropdownOpen ? 'var(--chat-accent-subtle)' : 'transparent'};
              color: {filesDropdownOpen ? 'var(--chat-accent)' : 'var(--chat-text-muted)'};
              min-width: 40px;
            "
            title="Connected Workbooks"
          >
            <Link2 size={13} style="flex-shrink: 0; transform: rotate(-45deg);" />
            {#if $runtimeState.connectedWorkbooks.length > 0}
              <span class="truncate" style="max-width: 100px; font-size: 11px;">
                {$runtimeState.connectedWorkbooks.find(w => w.isCurrent)?.name || "Connected"}
              </span>
            {/if}
            {#if $runtimeState.connectedWorkbooks.length > 1}
              <span style="
                background: var(--chat-accent); color: white;
                font-size: 8px; padding: 0 4px; border-radius: 99px;
                min-width: 12px; height: 12px;
                display: flex; align-items: center; justify-content: center;
              ">{$runtimeState.connectedWorkbooks.length}</span>
            {/if}
          </button>

          {#if filesDropdownOpen}
            <div
              role="menu"
              tabindex="-1"
              class="fluent-fade-in absolute top-full left-0 mt-1 flex flex-col"
              style="
                width: 260px;
                background: var(--chat-bg-tertiary);
                border: 1px solid var(--chat-border-active);
                border-radius: var(--chat-radius-lg);
                box-shadow: var(--chat-shadow-lg);
                z-index: 100;
                overflow: hidden;
                padding: 4px;
              "
              onmouseleave={() => (filesDropdownOpen = false)}
            >
              <div class="px-3 py-2 text-[10px] font-bold uppercase tracking-wider" style="color: var(--chat-text-muted);">
                {t("connectedWorkbooks") || "Connected Workbooks"}
              </div>
              
              <div style="max-height: 300px; overflow-y: auto;">
                {#each $runtimeState.connectedWorkbooks as wb (wb.id)}
                  <div 
                    class="flex items-center gap-3 px-3 py-2.5 transition-all"
                    style="
                      border-radius: var(--chat-radius);
                      background: {wb.isCurrent ? 'var(--chat-accent-subtle)' : 'transparent'};
                    "
                  >
                    <FileSpreadsheet size={16} style="color: {wb.isCurrent ? 'var(--chat-accent)' : 'var(--chat-text-muted)'}; flex-shrink: 0;" />
                    <div class="flex-1 min-width-0">
                      <div class="text-xs font-semibold truncate" style="color: {wb.isCurrent ? 'var(--chat-text-primary)' : 'var(--chat-text-secondary)'};">
                        {wb.name}
                      </div>
                      <div class="text-[9px]" style="color: var(--chat-text-muted);">
                        {wb.isCurrent ? (t("activeNow") || "Active now") : (t("connected") || "Connected")}
                      </div>
                    </div>
                    {#if wb.isCurrent}
                      <div style="width: 6px; height: 6px; border-radius: 99px; background: var(--chat-accent);"></div>
                    {/if}
                  </div>
                {/each}
              </div>
            </div>
          {/if}
        </div>
      </nav>

      <!-- Right Controls (delete, logout) — reserve ~40px gutter so Excel's
           task-pane chrome (back arrow / pin / close) doesn't overlap the
           rightmost button. -->
      <div class="flex items-center gap-0.5" style="padding-right: 40px;">
        {#if activeTab === "chat" && $runtimeState.messages.length > 0}
          <button
            type="button"
            onclick={() => controller.deleteCurrentSession()}
            class="flex items-center justify-center transition-all"
            style="width: 32px; height: 32px; border-radius: var(--chat-radius); color: var(--chat-text-muted); background: transparent;"
            title={t("deleteChat")}
            onmouseenter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(241,112,122,0.12)'; (e.currentTarget as HTMLElement).style.color = 'var(--chat-error)'; }}
            onmouseleave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'var(--chat-text-muted)'; }}
          >
            <Trash2 size={14} />
          </button>
        {/if}

        <!-- Logout -->
        <button
          type="button"
          onclick={() => authStore.logout()}
          class="flex items-center justify-center transition-all"
          style="width: 32px; height: 32px; border-radius: var(--chat-radius); color: var(--chat-text-muted); background: transparent;"
          title={`${t("signOut")} (${$authStore.user?.username})`}
          onmouseenter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(241,112,122,0.12)'; (e.currentTarget as HTMLElement).style.color = 'var(--chat-error)'; }}
          onmouseleave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'var(--chat-text-muted)'; }}
        >
          <LogOut size={14} />
        </button>
      </div>
    </div>
  </header>

  <!-- Main Content -->
  <main class="flex-1 flex flex-col min-h-0 overflow-hidden" style="background: var(--chat-bg);">
    {#if activeTab === "chat"}
      <MessageList />
      <ChatInput />
    {:else if activeTab === "files"}
      <div class="flex-1 flex flex-col min-h-0 bg-white dark:bg-[#1e1e1e] relative">
        <div style="padding: 16px; border-bottom: 1px solid var(--chat-border);">
          <div style="font-size: 14px; font-weight: 700; color: var(--chat-text-primary); margin-bottom: 4px;">{t("sessionFiles")}</div>
          <div style="font-size: 11px; color: var(--chat-text-muted);">{t("attachmentsDescription")}</div>
        </div>
        
        <div style="flex: 1; overflow-y: auto; padding: 12px;">
          {#if $runtimeState.uploads.length === 0}
            <div style="height: 100%; display: flex; flex-direction: column; items-center; justify-content: center; opacity: 0.6; text-align: center;">
              <ImageIcon size={32} style="margin: 0 auto 12px; color: var(--chat-text-muted);" />
              <div style="font-size: 13px; color: var(--chat-text-muted);">{t("noDocuments")}</div>
            </div>
          {:else}
            <div style="display: flex; flex-direction: column; gap: 8px;">
              {#each $runtimeState.uploads as file, idx (file.id ? `inp-${file.id}-${idx}` : `inp-up-${idx}`)}
                <div 
                  role="listitem"
                  style="
                    display: flex; align-items: center; gap: 12px;
                    padding: 10px 12px;
                    background: var(--chat-bg-secondary);
                    border: 1px solid var(--chat-border);
                    border-radius: var(--chat-radius-lg);
                    transition: background 0.1s;
                  "
                >
                  <div style="
                    width: 36px; height: 36px; border-radius: 8px;
                    background: var(--chat-bg-tertiary);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--chat-accent);
                  ">
                    {#if file.name.endsWith('.pdf')}
                      <FileText size={18} />
                    {:else if file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')}
                      <FileSpreadsheet size={18} />
                    {:else}
                      <ImageIcon size={18} />
                    {/if}
                  </div>
                  <div style="flex: 1; min-width: 0;">
                    <div style="font-size: 12px; font-weight: 600; color: var(--chat-text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title={file.name}>
                      {file.name}
                    </div>
                    <div style="display: flex; align-items: center; gap: 8px; margin-top: 2px;">
                      <span style="font-size: 10px; color: var(--chat-text-muted); font-family: var(--chat-font-mono);">
                        {Math.round(file.size / 1024)} KB
                      </span>
                      {#if file.extractedData}
                        <div style="display: flex; align-items: center; gap: 3px; font-size: 10px; color: #107c10; font-weight: 600;">
                          <div style="width: 4px; height: 4px; border-radius: 99px; background: #107c10;"></div>
                          Extracted
                        </div>
                      {/if}
                    </div>
                  </div>
                  <div class="flex items-center gap-1">
                    {#if file.extractedData}
                      <button 
                        type="button"
                        title="Preview data"
                        style="padding: 6px; border-radius: 6px; background: transparent; color: var(--chat-text-muted);"
                        onclick={() => previewFile = file}
                        onmouseenter={(e) => (e.currentTarget.style.background = 'var(--chat-bg-overlay)')}
                        onmouseleave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <Eye size={14} />
                      </button>
                    {/if}
                    <a 
                      href={file.id ? controller.getDownloadUrl(file.id) : '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={file.name}
                      title="Download original"
                      style="padding: 6px; border-radius: 6px; background: transparent; color: var(--chat-text-muted); display: flex;"
                      onmouseenter={(e) => (e.currentTarget.style.background = 'var(--chat-bg-overlay)')}
                      onmouseleave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <Download size={14} />
                    </a>
                    <button 
                      type="button"
                      title="Delete file"
                      style="padding: 6px; border-radius: 6px; background: transparent; color: var(--chat-text-muted);"
                      onclick={() => file.id && controller.deleteAttachment(file.id)}
                      onmouseenter={(e) => { (e.currentTarget.style.background = 'rgba(241,112,122,0.12)'); (e.currentTarget.style.color = 'var(--chat-error)'); }}
                      onmouseleave={(e) => { (e.currentTarget.style.background = 'transparent'); (e.currentTarget.style.color = 'var(--chat-text-muted)'); }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              {/each}
            </div>
          {/if}
        </div>

        <!-- Preview Modal Overlay -->
        {#if previewFile}
          <div style="
            position: absolute; inset: 0; z-index: 100;
            background: rgba(0,0,0,0.4);
            backdrop-filter: blur(4px);
            display: flex; align-items: center; justify-content: center;
            padding: 20px;
          ">
            <div class="fluent-fade-in" style="
              width: 100%; height: 100%; max-height: 80vh;
              background: var(--chat-bg);
              border: 1px solid var(--chat-border-active);
              border-radius: var(--chat-radius-lg);
              display: flex; flex-direction: column;
              box-shadow: var(--chat-shadow-lg);
            ">
              <div style="padding: 12px 16px; border-bottom: 1px solid var(--chat-border); display: flex; align-items: center; justify-content: space-between;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <FileSearch size={16} style="color: var(--chat-accent);" />
                  <span style="font-size: 13px; font-weight: 700; color: var(--chat-text-primary);">{previewFile.name}</span>
                </div>
                <button 
                  type="button"
                  onclick={() => previewFile = null}
                  style="font-size: 20px; line-height: 1; color: var(--chat-text-muted); background: transparent;"
                >&times;</button>
              </div>
              <div style="flex: 1; overflow-y: auto; padding: 16px; font-family: var(--chat-font-mono); font-size: 11px; color: var(--chat-text-secondary); line-height: 1.6; white-space: pre-wrap; background: var(--chat-bg-secondary);">
                {previewFile.extractedData}
              </div>
              <div style="padding: 12px; border-top: 1px solid var(--chat-border); display: flex; justify-content: flex-end;">
                <button 
                  type="button"
                  onclick={() => previewFile = null}
                  style="
                    padding: 6px 16px; border-radius: var(--chat-radius);
                    background: var(--chat-accent); color: white;
                    font-size: 11px; font-weight: 600;
                  "
                >Close</button>
              </div>
            </div>
          </div>
        {/if}
      </div>
    {:else if activeTab === "settings"}
      <SettingsPanel
        serverLlmInfo={$runtimeState.serverLlmInfo}
        onRefresh={() => controller.refreshServerLlmInfo()}
        theme={theme}
        toggleTheme={toggleTheme}
      />
    {:else if activeTab === "debug"}
      <div class="flex-1 flex flex-col min-h-0 bg-white dark:bg-[#1e1e1e] relative">
        <div style="padding: 16px; border-bottom: 1px solid var(--chat-border); display: flex; align-items: center; justify-content: space-between;">
          <div>
            <div style="font-size: 14px; font-weight: 700; color: var(--chat-text-primary); margin-bottom: 4px;">{t("debug")}</div>
            <div style="font-size: 11px; color: var(--chat-text-muted);">Inspect sheet structure and diagnostic data</div>
          </div>
          <button
            type="button"
            onclick={runStructureAnalysis}
            disabled={isAnalyzing}
            style="
              padding: 8px 16px;
              background: var(--chat-accent);
              color: white;
              border-radius: var(--chat-radius);
              font-size: 12px;
              font-weight: 600;
              opacity: {isAnalyzing ? 0.6 : 1};
            "
          >
            {isAnalyzing ? "Analyzing..." : "Analyze Sheet Structure"}
          </button>
        </div>

        <div class="flex-1 relative overflow-hidden" style="margin: 12px; display: flex; flex-direction: column;">
          {#if debugOutput}
            <div style="position: absolute; top: 12px; right: 12px; z-index: 10;">
              <button
                type="button"
                onclick={copyToClipboard}
                style="
                  padding: 8px;
                  background: var(--chat-bg-tertiary);
                  border: 1px solid var(--chat-border);
                  border-radius: var(--chat-radius);
                  color: var(--chat-text-secondary);
                  display: flex;
                  align-items: center;
                  gap: 6px;
                  font-size: 11px;
                "
              >
                {#if showCopyFeedback}
                  <Check size={14} style="color: #107c10;" />
                  <span>Copied!</span>
                {:else}
                  <Copy size={14} />
                  <span>Copy JSON</span>
                {/if}
              </button>
            </div>
            <pre style="
              flex: 1;
              background: var(--chat-bg-secondary);
              border: 1px solid var(--chat-border);
              border-radius: var(--chat-radius-lg);
              padding: 16px;
              font-family: var(--chat-font-mono);
              font-size: 11px;
              line-height: 1.5;
              color: var(--chat-text-secondary);
              overflow: auto;
              white-space: pre-wrap;
              word-break: break-all;
            ">{debugOutput}</pre>
          {:else}
            <div style="flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; opacity: 0.5;">
              <Bug size={48} style="margin-bottom: 16px; color: var(--chat-text-muted);" />
              <div style="font-size: 13px; color: var(--chat-text-muted);">Run analysis to see results</div>
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </main>

  <!-- Bottom Navigation & Footer -->
  <footer style="
    display: flex; flex-direction: column;
    background: var(--chat-bg-secondary);
    border-top: 1px solid var(--chat-border);
    flex-shrink: 0;
    z-index: 20;
  ">
    <!-- Navigation Tabs -->
    <nav class="flex items-center justify-around px-2 py-0.5" style="border-bottom: 1px solid var(--chat-border);">
      <button
        type="button"
        onclick={() => switchToTab("chat")}
        class="flex items-center gap-2 py-2 px-3 transition-all"
        style="color: {activeTab === 'chat' ? 'var(--chat-accent)' : 'var(--chat-text-muted)'};"
      >
        <MessageSquare size={14} />
        <span style="font-size: 11px; font-weight: 600;">{t("chat")}</span>
      </button>

      <button
        type="button"
        onclick={() => switchToTab("files")}
        class="flex items-center gap-2 py-2 px-3 transition-all"
        style="color: {activeTab === 'files' ? 'var(--chat-accent)' : 'var(--chat-text-muted)'};"
      >
        <FolderOpen size={14} />
        <span style="font-size: 11px; font-weight: 600;">{t("files")}</span>
      </button>

      <button
        type="button"
        onclick={() => switchToTab("settings")}
        class="flex items-center gap-2 py-2 px-3 transition-all"
        style="color: {activeTab === 'settings' ? 'var(--chat-accent)' : 'var(--chat-text-muted)'};"
      >
        <Settings size={14} />
        <span style="font-size: 11px; font-weight: 600;">{t("settings")}</span>
      </button>

      <button
        type="button"
        onclick={() => switchToTab("debug")}
        class="flex items-center gap-2 py-2 px-3 transition-all"
        style="color: {activeTab === 'debug' ? 'var(--chat-accent)' : 'var(--chat-text-muted)'};"
      >
        <Bug size={14} />
        <span style="font-size: 11px; font-weight: 600;">{t("debug")}</span>
      </button>
    </nav>

    <!-- Stats Bar -->
    {#if activeTab === "chat" && ($authStore.user?.isAdmin || $runtimeState.serverLlmInfo?.showAdvancedStats)}
      <div style="
        display: flex; align-items: center; justify-content: space-between;
        padding: 4px 12px;
        font-family: var(--chat-font-mono);
        font-size: 9px;
        color: var(--chat-text-muted);
      ">
        <div style="display: flex; align-items: center; gap: 8px;">
          <span title="Commands" style="color: var(--chat-accent); font-weight: 600;">CMD {$runtimeState.sessionStats.commandCount}</span>
          <span title="Input Tokens">↑{formatTokens($runtimeState.sessionStats.inputTokens)}</span>
          <span title="Output Tokens">↓{formatTokens($runtimeState.sessionStats.outputTokens)}</span>
          <span title="Total Cost" style="color: var(--chat-text-secondary);">{formatCost($runtimeState.sessionStats.totalCost)}</span>
        </div>
        {#if $runtimeState.serverLlmInfo}
          <div style="display: flex; align-items: center; gap: 3px; text-transform: uppercase; font-weight: 600; max-width: 50%; overflow: hidden;">
            <span style="color: var(--chat-accent); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{$runtimeState.serverLlmInfo.provider}</span>
            <span style="opacity: 0.4;">•</span>
            <span title={$runtimeState.serverLlmInfo.model} style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{$runtimeState.serverLlmInfo.model}</span>
          </div>
        {/if}
      </div>
    {/if}
  </footer>

  <!-- Drag Overlay -->
  {#if isDragOver}
    <div style="
      position: absolute; inset: 0; z-index: 50;
      display: flex; align-items: center; justify-content: center;
      background: rgba(28,28,28,0.85);
      backdrop-filter: blur(8px);
      pointer-events: none;
    ">
      <div style="
        display: flex; flex-direction: column; align-items: center; gap: 12px;
        padding: 32px 40px;
        border: 2px dashed var(--chat-accent);
        border-radius: var(--chat-radius-lg);
        background: var(--chat-accent-subtle);
      ">
        <Upload size={28} style="color: var(--chat-accent);" class="animate-bounce" />
        <span style="font-size: 12px; font-weight: 600; letter-spacing: 0.05em; color: var(--chat-text-primary); text-transform: uppercase;">Drop files to attach</span>
      </div>
    </div>
  {/if}
</div>

<style>
  :global(:root) { transition: background-color 0.2s ease, color 0.2s ease; }

  button { cursor: pointer; border: none; outline: none; }
  button:focus-visible {
    outline: 2px solid var(--chat-accent);
    outline-offset: 2px;
  }
</style>
